import * as THREE from 'three';
import type { ArenaXZ, BoxCollider, ParticleTier } from './types';
import { BRICK_PROFILE, CONCRETE_PROFILE, STONE_PROFILE } from './profiles';
import { GRADE_GLSL } from './gradeGLSL';

/** Height (m) where the dusting regime finishes ramping in. */
export const GROUND_FILM_H = 0.008;
/** Height (m) where full pile tint takes over. */
export const GROUND_PILE_H = 0.02;
/** Peak mix strength for thin-film dusting (before pileGate). */
export const GROUND_DUST_STRENGTH = 0.3;
/** Siege stain: height where the multiply edge begins (m). */
export const GROUND_STAIN_EDGE0 = 0.0002;
/** Siege stain: height where the multiply edge finishes (m). */
export const GROUND_STAIN_EDGE1 = 0.024;
/** Siege stain: cap on premultiplied amt so dest luma stays within ~20%. */
export const GROUND_STAIN_AMT_CAP = 0.68;

const SAMPLE_HEIGHT_GLSL = /* glsl */ `
// Visual height = granular R + film A. Film is a coating, not a pile.
float texelDeposit(vec4 s) {
  float r = (s.r == s.r && abs(s.r) < 1.0e6) ? max(s.r, 0.0) : 0.0;
  float a = (s.a == s.a && abs(s.a) < 1.0e6) ? max(s.a, 0.0) : 0.0;
  return r + a;
}

// Manual 4-tap bilinear — half-float VTF linear filtering is not reliable on
// every WebGL2 driver; nearest VTF turns every texel into a square pillow.
float sampleHeightBilinear(sampler2D tex, vec2 uv) {
  vec2 size = vec2(1.0 / max(uHfTexel.x, 1.0e-6), 1.0 / max(uHfTexel.y, 1.0e-6));
  vec2 coord = uv * size - 0.5;
  vec2 f = fract(coord);
  vec2 i0 = floor(coord);
  vec2 uv00 = (i0 + 0.5) * uHfTexel;
  float h00 = texelDeposit(texture2D(tex, uv00));
  float h10 = texelDeposit(texture2D(tex, uv00 + vec2(uHfTexel.x, 0.0)));
  float h01 = texelDeposit(texture2D(tex, uv00 + vec2(0.0, uHfTexel.y)));
  float h11 = texelDeposit(texture2D(tex, uv00 + uHfTexel));
  float h0 = mix(h00, h10, f.x);
  float h1 = mix(h01, h11, f.x);
  return mix(h0, h1, f.y);
}
`;

const GROUND_VERT = /* glsl */ `
uniform sampler2D tHeight;
uniform sampler2D tBase;
uniform vec2 uArenaMin;
uniform vec2 uArenaSize;
uniform vec2 uHfTexel;
uniform float uDisplace;
varying vec2 vUv;
varying vec3 vWorld;
varying vec3 vView;
${SAMPLE_HEIGHT_GLSL}
void main() {
  // Flip V so UV-mapped XZ winding faces +Y (PlaneGeometry is +Z / FrontSide).
  vUv = vec2(uv.x, 1.0 - uv.y);
  vec3 pos = vec3(
    mix(uArenaMin.x, uArenaMin.x + uArenaSize.x, vUv.x),
    0.0,
    mix(uArenaMin.y, uArenaMin.y + uArenaSize.y, vUv.y)
  );
  // Floor sheet only — box footprints stay at y=0 so the box mesh owns them.
  float base = texture2D(tBase, vUv).r;
  if (uDisplace > 0.5 && base <= 1.0e-4) {
    float hy = sampleHeightBilinear(tHeight, vUv);
    pos.y += (hy == hy) ? max(hy, 0.0) : 0.0;
  }
  vec4 world = modelMatrix * vec4(pos, 1.0);
  vWorld = world.xyz;
  vView = (viewMatrix * world).xyz;
  gl_Position = projectionMatrix * viewMatrix * world;
}
`;

const GROUND_FRAG = /* glsl */ `
uniform sampler2D tHeight;
uniform sampler2D tBase;
uniform sampler2D tShadow;
uniform vec3 uGroundColor;
uniform vec3 uBrickTint;
uniform vec3 uConcreteTint;
uniform vec3 uStoneTint;
uniform vec3 uLightDir;
uniform vec3 uLightColor;
uniform vec3 uAmbient;
uniform vec2 uTexelWorld;
uniform vec2 uHfTexel;
uniform float uDisplace;
uniform float uShadowStrength;
uniform float uShadowCap;
uniform vec3 uShadowMul;
uniform vec3 uFilmTint;
uniform float uDustCover;
uniform float uDepositStain;
uniform float uUseRendererGrade;
uniform vec3 uSceneFogColor;
uniform float uSceneFogDensity;
uniform float uDepositOnly;
varying vec2 vUv;
varying vec3 vWorld;
varying vec3 vView;
${GRADE_GLSL}
${SAMPLE_HEIGHT_GLSL}

float finiteOrZero(float v) {
  return (v == v && abs(v) < 1.0e6) ? v : 0.0;
}

void main() {
  float base = texture2D(tBase, vUv).r;
  // Mask deposits on box footprints — those render on the box-top patch.
  vec4 hf = texture2D(tHeight, vUv);
  float h = base > 1.0e-4 ? 0.0 : texelDeposit(hf);
  vec2 texel = uHfTexel;
  float hL = base > 1.0e-4 ? 0.0 : texelDeposit(texture2D(tHeight, vUv - vec2(texel.x, 0.0)));
  float hR = base > 1.0e-4 ? 0.0 : texelDeposit(texture2D(tHeight, vUv + vec2(texel.x, 0.0)));
  float hD = base > 1.0e-4 ? 0.0 : texelDeposit(texture2D(tHeight, vUv - vec2(0.0, texel.y)));
  float hU = base > 1.0e-4 ? 0.0 : texelDeposit(texture2D(tHeight, vUv + vec2(0.0, texel.y)));
  // uTexelWorld=0 (unset / failed path) would make normalize(0,0,0)=NaN → black.
  float ny = max(2.0 * max(uTexelWorld.x, 1.0e-4), 1.0e-4);
  vec3 n = normalize(vec3(hL - hR, ny, hD - hU));
  if (!(dot(n, n) > 0.25)) n = vec3(0.0, 1.0, 0.0);

  // Hard height cut is particle-lab / box-top only. Siege stain fades instead.
  if (uDepositOnly > 0.5 && uDepositStain < 0.5 && h < 4.0e-4) discard;

  float mixId = h > 1.0e-5 ? clamp(finiteOrZero(hf.g) / h, 0.0, 1.0) : 0.0;
  vec3 pile = mixId < 0.5
    ? mix(uBrickTint, uConcreteTint, mixId * 2.0)
    : mix(uConcreteTint, uStoneTint, (mixId - 0.5) * 2.0);
  // Two-regime tint: thin films (<~8mm) read as patchy desaturated dusting
  // (max ~32% strength); full pile colour only above ~2cm so talus/grain
  // piles keep their prior look. Old smoothstep(0, 0.018, h) painted a
  // 4mm film ~full-material across the whole cloud footprint.
  float filmAmt = smoothstep(0.0, 0.008, h);
  float pileGate = smoothstep(0.008, 0.020, h);
  float luma = dot(pile, vec3(0.2126, 0.7152, 0.0722));
  // Heavy desat toward luma so films read as dusty concrete, not wet paint.
  vec3 dustTint = mix(vec3(luma), pile, 0.22);
  if (dot(uFilmTint, uFilmTint) > 1.0e-6) dustTint = uFilmTint;
  // Low-frequency grit from UV only — no time, band-limited so idle is stable.
  float gritN = sin(vUv.x * 48.0) * sin(vUv.y * 42.0);
  float grit = 0.012 * gritN;
  float patchy = mix(0.28, 1.0, 0.5 + 0.5 * gritN);
  float dustCover = uDustCover * filmAmt * patchy;
  float cover = mix(dustCover, 1.0, pileGate);
  vec3 tintCol = mix(dustTint, pile, pileGate);
  vec3 albedo = mix(uGroundColor + grit, tintCol, cover);
  float sh = clamp(texture2D(tShadow, vUv).r, 0.0, 1.0);
  float shAmt = uShadowStrength * min(sh, uShadowCap);
  albedo *= mix(vec3(1.0), uShadowMul, shAmt);

  // Siege deposit-only: premultiplied framebuffer multiply of a warm soil
  // stain. three r183 MultiplyBlending is a no-op unless premultipliedAlpha
  // is set; we output dest * mix(1, soil, amt) via PMA (src*dst + dst*(1-a)).
  // Particle-lab leaves uDepositStain at 0.
  if (uDepositStain > 0.5) {
    float hS = base > 1.0e-4 ? 0.0 : sampleHeightBilinear(tHeight, vUv);
    float hBlur = 0.25 * (
      (base > 1.0e-4 ? 0.0 : sampleHeightBilinear(tHeight, vUv + vec2(uHfTexel.x, 0.0))) +
      (base > 1.0e-4 ? 0.0 : sampleHeightBilinear(tHeight, vUv - vec2(uHfTexel.x, 0.0))) +
      (base > 1.0e-4 ? 0.0 : sampleHeightBilinear(tHeight, vUv + vec2(0.0, uHfTexel.y))) +
      (base > 1.0e-4 ? 0.0 : sampleHeightBilinear(tHeight, vUv - vec2(0.0, uHfTexel.y)))
    );
    float hFar = 0.25 * (
      (base > 1.0e-4 ? 0.0 : sampleHeightBilinear(tHeight, vUv + vec2(2.0 * uHfTexel.x, 0.0))) +
      (base > 1.0e-4 ? 0.0 : sampleHeightBilinear(tHeight, vUv - vec2(2.0 * uHfTexel.x, 0.0))) +
      (base > 1.0e-4 ? 0.0 : sampleHeightBilinear(tHeight, vUv + vec2(0.0, 2.0 * uHfTexel.y))) +
      (base > 1.0e-4 ? 0.0 : sampleHeightBilinear(tHeight, vUv - vec2(0.0, 2.0 * uHfTexel.y)))
    );
    float hEdge = 0.34 * hS + 0.38 * hBlur + 0.28 * hFar;
    vec3 soil = dot(uFilmTint, uFilmTint) > 1.0e-6 ? uFilmTint : vec3(0.82, 0.70, 0.52);
    float sn1 = sin(vUv.x * 48.0) * sin(vUv.y * 42.0);
    float sn2 = sin(vUv.x * 121.0 + 1.7) * sin(vUv.y * 89.0 + 0.9);
    float sn3 = sin(vUv.x * 17.0 + vUv.y * 23.0);
    float stainN = 0.50 * sn1 + 0.32 * sn2 + 0.18 * sn3;
    float stainEdge = smoothstep(${GROUND_STAIN_EDGE0.toFixed(4)}, ${GROUND_STAIN_EDGE1.toFixed(3)}, hEdge);
    float filmAmtS = smoothstep(0.0005, 0.009, hS);
    float pileGateS = smoothstep(0.008, 0.026, hS);
    float patchyS = mix(0.42, 1.0, 0.5 + 0.5 * stainN);
    float dustCoverS = uDustCover * filmAmtS * patchyS;
    float coverS = mix(dustCoverS, 0.68, pileGateS);
    float amt = clamp(stainEdge * coverS, 0.0, ${GROUND_STAIN_AMT_CAP.toFixed(2)});
    if (amt < 0.01) discard;
    vec3 soilV = soil * (1.0 + 0.20 * stainN);
    soilV = clamp(soilV, vec3(0.68, 0.56, 0.40), vec3(0.94, 0.86, 0.72));
    soilV *= mix(vec3(1.0), uShadowMul, shAmt);
    gl_FragColor = vec4(soilV * amt, amt);
    return;
  }

  vec3 L = normalize(uLightDir);
  float ndl = max(dot(n, L), 0.0);
  float vLen2 = max(dot(vView, vView), 1.0e-8);
  vec3 V = -vView * inversesqrt(vLen2);
  vec3 H = normalize(L + V);
  float rough = 0.72 + 0.22 * cover;
  float spec = pow(max(dot(n, H), 0.0), mix(48.0, 10.0, rough)) * (1.0 - rough) * 0.18;
  vec3 col = albedo * (uAmbient + uLightColor * ndl) + uLightColor * spec;
  if (uDepositOnly < 0.5) {
    // Hairline arena rim so the playfield reads against the void on a phone.
    float edge = min(min(vUv.x, 1.0 - vUv.x), min(vUv.y, 1.0 - vUv.y));
    float rim = 1.0 - smoothstep(0.0, 0.014, edge);
    col = mix(col, col * 0.55 + vec3(0.16, 0.17, 0.19), rim);
  }
  if (uUseRendererGrade > 0.5) {
    if (uSceneFogDensity > 0.0) {
      float f = 1.0 - exp(-uSceneFogDensity * length(vWorld - cameraPosition));
      col = mix(col, uSceneFogColor, clamp(f, 0.0, 1.0));
    }
  } else {
    col = plFog(plGrade(col), vWorld);
  }
  // Same encoding MeshStandardMaterial uses — without this, linear albedo
  // is shown as sRGB and the floor reads almost black.
  gl_FragColor = linearToOutputTexel(vec4(col, 1.0));
}
`;

const PATCH_VERT = /* glsl */ `
uniform sampler2D tHeight;
uniform vec2 uArenaMin;
uniform vec2 uArenaSize;
uniform vec2 uHfTexel;
uniform float uDisplace;
varying vec2 vUv;
varying vec3 vWorld;
varying vec3 vView;
${SAMPLE_HEIGHT_GLSL}
void main() {
  vec4 world0 = modelMatrix * vec4(position, 1.0);
  vUv = (world0.xz - uArenaMin) / uArenaSize;
  float hy = 0.0;
  if (uDisplace > 0.5) {
    float s = sampleHeightBilinear(tHeight, vUv);
    hy = (s == s) ? max(s, 0.0) : 0.0;
  }
  vec3 wp = vec3(world0.x, world0.y + hy, world0.z);
  vWorld = wp;
  vec4 view = viewMatrix * vec4(wp, 1.0);
  vView = view.xyz;
  gl_Position = projectionMatrix * view;
}
`;

const PATCH_FRAG = /* glsl */ `
uniform sampler2D tHeight;
uniform vec3 uBrickTint;
uniform vec3 uConcreteTint;
uniform vec3 uStoneTint;
uniform vec3 uLightDir;
uniform vec3 uLightColor;
uniform vec3 uAmbient;
uniform vec2 uTexelWorld;
uniform vec2 uHfTexel;
varying vec2 vUv;
varying vec3 vWorld;
varying vec3 vView;
${GRADE_GLSL}
${SAMPLE_HEIGHT_GLSL}

float finiteOrZero(float v) {
  return (v == v && abs(v) < 1.0e6) ? v : 0.0;
}

void main() {
  vec4 hf = texture2D(tHeight, vUv);
  float h = texelDeposit(hf);
  // Hide the patch where there is no pile so the box top shows through.
  if (h < 4.0e-4) discard;
  vec2 texel = uHfTexel;
  float hL = texelDeposit(texture2D(tHeight, vUv - vec2(texel.x, 0.0)));
  float hR = texelDeposit(texture2D(tHeight, vUv + vec2(texel.x, 0.0)));
  float hD = texelDeposit(texture2D(tHeight, vUv - vec2(0.0, texel.y)));
  float hU = texelDeposit(texture2D(tHeight, vUv + vec2(0.0, texel.y)));
  float ny = max(2.0 * max(uTexelWorld.x, 1.0e-4), 1.0e-4);
  vec3 n = normalize(vec3(hL - hR, ny, hD - hU));
  if (!(dot(n, n) > 0.25)) n = vec3(0.0, 1.0, 0.0);

  float mixId = h > 1.0e-5 ? clamp(finiteOrZero(hf.g) / h, 0.0, 1.0) : 0.0;
  vec3 pile = mixId < 0.5
    ? mix(uBrickTint, uConcreteTint, mixId * 2.0)
    : mix(uConcreteTint, uStoneTint, (mixId - 0.5) * 2.0);
  float grit = 0.012 * sin(vUv.x * 48.0) * sin(vUv.y * 42.0);
  vec3 albedo = pile + grit;

  vec3 L = normalize(uLightDir);
  float ndl = max(dot(n, L), 0.0);
  float vLen2 = max(dot(vView, vView), 1.0e-8);
  vec3 V = -vView * inversesqrt(vLen2);
  vec3 H = normalize(L + V);
  float spec = pow(max(dot(n, H), 0.0), 10.0) * 0.05;
  vec3 col = albedo * (uAmbient + uLightColor * ndl) + uLightColor * spec;
  col = plFog(plGrade(col), vWorld);
  gl_FragColor = linearToOutputTexel(vec4(col, 1.0));
}
`;

export function makeDummyHeightTexture(): THREE.DataTexture {
  const tex = new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  tex.name = 'particle-lab-height-dummy';
  return tex;
}

export function createGroundMesh(
  heightTex: THREE.Texture | null,
  arena: ArenaXZ,
  tier: ParticleTier,
  hfSize = 512,
  baseTex: THREE.Texture | null = null,
  colliders: readonly BoxCollider[] = [],
): THREE.Mesh {
  const segs = tier === 'mobile' ? 32 : 192;
  const geom = new THREE.PlaneGeometry(1, 1, segs, segs);
  const safeHf = Number.isFinite(hfSize) && hfSize > 0 ? hfSize : 256;
  const texelX = arena.sizeX / safeHf;
  const texelZ = arena.sizeZ / safeHf;
  const hf = heightTex ?? makeDummyHeightTexture();
  const base = baseTex ?? makeDummyHeightTexture();
  const dummyShadow = makeDummyHeightTexture();
  dummyShadow.name = 'particle-lab-shadow-dummy';
  const uniforms = {
    tHeight: { value: hf },
    tBase: { value: base },
    tShadow: { value: dummyShadow as THREE.Texture },
    uArenaMin: { value: new THREE.Vector2(arena.minX, arena.minZ) },
    uArenaSize: { value: new THREE.Vector2(arena.sizeX, arena.sizeZ) },
    uDisplace: { value: tier === 'desktop' ? 1 : 0 },
    uGroundColor: { value: new THREE.Color(0x5a606a) },
    uBrickTint: { value: new THREE.Color().fromArray([...BRICK_PROFILE.pileTint]) },
    uConcreteTint: { value: new THREE.Color().fromArray([...CONCRETE_PROFILE.pileTint]) },
    uStoneTint: { value: new THREE.Color().fromArray([...STONE_PROFILE.pileTint]) },
    uLightDir: { value: new THREE.Vector3(0.35, 0.88, 0.32).normalize() },
    uLightColor: { value: new THREE.Color(0xfff1d6) },
    uAmbient: { value: new THREE.Color(0x5a6270) },
    uTexelWorld: { value: new THREE.Vector2(texelX, texelZ) },
    uHfTexel: { value: new THREE.Vector2(1 / safeHf, 1 / safeHf) },
    uShadowStrength: { value: 0.58 },
    uShadowCap: { value: 0.72 },
    uShadowMul: { value: new THREE.Vector3(0, 0, 0) },
    uFilmTint: { value: new THREE.Vector3(0, 0, 0) },
    uDustCover: { value: GROUND_DUST_STRENGTH },
    uDepositStain: { value: 0 },
    uUseRendererGrade: { value: 0 },
    uSceneFogColor: { value: new THREE.Vector3(0.035, 0.038, 0.048) },
    uSceneFogDensity: { value: 0 },
    uDepositOnly: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: GROUND_VERT,
    fragmentShader: GROUND_FRAG,
    // Belt-and-suspenders: winding is flipped to +Y, but never let the floor vanish.
    side: THREE.DoubleSide,
    depthWrite: true,
    depthTest: true,
  });
  mat.toneMapped = false;
  const mesh = new THREE.Mesh(geom, mat);
  mesh.name = 'particle-lab-ground';
  mesh.frustumCulled = false;
  mesh.receiveShadow = false;

  const cx = arena.minX + arena.sizeX * 0.5;
  const cz = arena.minZ + arena.sizeZ * 0.5;
  const underGeom = new THREE.PlaneGeometry(arena.sizeX + 10, arena.sizeZ + 10);
  underGeom.rotateX(-Math.PI / 2);
  const under = new THREE.Mesh(
    underGeom,
    new THREE.MeshBasicMaterial({ color: 0x14161c, depthWrite: true }),
  );
  under.name = 'particle-lab-void';
  under.position.set(cx, -0.03, cz);
  under.frustumCulled = false;
  mesh.add(under);

  const minX = arena.minX;
  const maxX = arena.minX + arena.sizeX;
  const minZ = arena.minZ;
  const maxZ = arena.minZ + arena.sizeZ;
  const edgeGeom = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(minX, 0.012, minZ),
    new THREE.Vector3(maxX, 0.012, minZ),
    new THREE.Vector3(maxX, 0.012, maxZ),
    new THREE.Vector3(minX, 0.012, maxZ),
    new THREE.Vector3(minX, 0.012, minZ),
  ]);
  const edge = new THREE.Line(
    edgeGeom,
    new THREE.LineBasicMaterial({ color: 0x5a616c, transparent: true, opacity: 0.7 }),
  );
  edge.name = 'particle-lab-arena-edge';
  edge.frustumCulled = false;
  mesh.add(edge);

  const patchUniforms = {
    tHeight: { value: hf },
    uArenaMin: { value: new THREE.Vector2(arena.minX, arena.minZ) },
    uArenaSize: { value: new THREE.Vector2(arena.sizeX, arena.sizeZ) },
    uDisplace: { value: tier === 'desktop' ? 1 : 0 },
    uBrickTint: { value: new THREE.Color().fromArray([...BRICK_PROFILE.pileTint]) },
    uConcreteTint: { value: new THREE.Color().fromArray([...CONCRETE_PROFILE.pileTint]) },
    uStoneTint: { value: new THREE.Color().fromArray([...STONE_PROFILE.pileTint]) },
    uLightDir: { value: new THREE.Vector3(0.35, 0.88, 0.32).normalize() },
    uLightColor: { value: new THREE.Color(0xfff1d6) },
    uAmbient: { value: new THREE.Color(0x3a404c) },
    uTexelWorld: { value: new THREE.Vector2(texelX, texelZ) },
    uHfTexel: { value: new THREE.Vector2(1 / safeHf, 1 / safeHf) },
  };
  const patchMat = new THREE.ShaderMaterial({
    uniforms: patchUniforms,
    vertexShader: PATCH_VERT,
    fragmentShader: PATCH_FRAG,
    side: THREE.FrontSide,
    depthWrite: true,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
  });
  patchMat.toneMapped = false;

  const dens = tier === 'mobile' ? 4 : 16;
  colliders.forEach((box, i) => {
    const w = box.half[0] * 2;
    const d = box.half[2] * 2;
    const segsW = Math.max(4, Math.round(w * dens));
    const segsD = Math.max(4, Math.round(d * dens));
    const pGeom = new THREE.PlaneGeometry(w, d, segsW, segsD);
    pGeom.rotateX(-Math.PI / 2);
    const patch = new THREE.Mesh(pGeom, patchMat);
    patch.position.set(box.center[0], box.center[1] + box.half[1] + 0.0015, box.center[2]);
    patch.name = `particle-lab-boxtop-${i}`;
    patch.frustumCulled = false;
    patch.renderOrder = 1;
    patch.raycast = () => {};
    mesh.add(patch);
  });

  (mesh.userData as { setHeightTexture?: (t: THREE.Texture) => void }).setHeightTexture = (t) => {
    uniforms.tHeight.value = t;
    patchUniforms.tHeight.value = t;
  };
  (mesh.userData as { setBaseTexture?: (t: THREE.Texture) => void }).setBaseTexture = (t) => {
    uniforms.tBase.value = t;
  };
  (mesh.userData as { setShadowTexture?: (t: THREE.Texture) => void }).setShadowTexture = (t) => {
    uniforms.tShadow.value = t;
  };
  return mesh;
}

export function syncGroundHeight(mesh: THREE.Mesh, texture: THREE.Texture): void {
  const fn = (mesh.userData as { setHeightTexture?: (t: THREE.Texture) => void }).setHeightTexture;
  fn?.(texture);
}

export function syncGroundShadow(mesh: THREE.Mesh, texture: THREE.Texture): void {
  const fn = (mesh.userData as { setShadowTexture?: (t: THREE.Texture) => void }).setShadowTexture;
  fn?.(texture);
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / Math.max(edge1 - edge0, 1e-12)));
  return t * t * (3 - 2 * t);
}

/**
 * CPU mirror of the ground fragment cover curve. `patch01` is the 0..1
 * mix factor into patchy (shader: 0.5 + 0.5 * sin*sin).
 */
export function groundCoverStrength(heightM: number, patch01 = 0.75): number {
  const h = Math.max(0, heightM);
  const filmAmt = smoothstep(0, GROUND_FILM_H, h);
  const pileGate = smoothstep(GROUND_FILM_H, GROUND_PILE_H, h);
  const patchy = 0.28 + 0.72 * Math.min(1, Math.max(0, patch01));
  const dustCover = GROUND_DUST_STRENGTH * filmAmt * patchy;
  return dustCover * (1 - pileGate) + pileGate;
}

/**
 * CPU mirror of the Siege stain multiply amount (0 = identity, cap = max
 * darken). `patch01` is the 0..1 grit mix; `dustCover` is uDustCover.
 */
export function groundStainAmount(heightM: number, patch01 = 0.75, dustCover = 0.88): number {
  const h = Math.max(0, heightM);
  const edge = smoothstep(GROUND_STAIN_EDGE0, GROUND_STAIN_EDGE1, h);
  const filmAmt = smoothstep(0.0005, 0.009, h);
  const pileGate = smoothstep(0.008, 0.026, h);
  const patchy = 0.42 + 0.58 * Math.min(1, Math.max(0, patch01));
  const cover = dustCover * filmAmt * patchy * (1 - pileGate) + 0.68 * pileGate;
  return Math.min(GROUND_STAIN_AMT_CAP, edge * cover);
}
